import React from "react";
import {
  Box,
  Grid,
  Button,
  Typography,
  Container,
  Card,
  CardContent,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import farmlandImg from "../assets/Agriculturalfarmland.png";
import yesicon from "../assets/yesicon.png";
import so from "../assets/so.png";
import mdo from "../assets/mdo.png";
import farmer from "../assets/farmer.png";
import Overlay from "../assets/Overlay.png";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import Visibility from '@mui/icons-material/Visibility';
import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import RadarOutlinedIcon from '@mui/icons-material/RadarOutlined';

const industryProblems = [
  
  {
    iconType: "img",
    icon: Overlay,
    label: "Farmer Data Disappear",
    desc: "When an MDO or SO leaves, years of farmer relationships vanish overnight.",
    aria: "user",
  },
  {
    iconType: "mui",
    icon: WarningAmberIcon,
    label: "Leads Are Lost",
    desc: "Promising leads and ongoing conversations are forgotten with every employee change.",
    aria: "alert",
  },
  {
    iconType: "mui",
    icon: TrackChangesIcon,
    label: "Commitments Forgotten",
    desc: "Verbal commitments and promises made to farmers are never tracked or transferred.",
    aria: "target",
  },
  {
    iconType: "mui",
    icon: TrendingDownIcon,
    label: "Targets Restart from Zero",
    desc: "Revenue leaks. Forecasting fails. Accountability drops with every transition.",
    aria: "restart",
  },
];

const gainItems = [
  {
    title: 'Better Forecasting',
    desc: 'Accurate predictions powered by continuous historical data.',
    icon: PollOutlinedIcon,
  },
  {
    title: 'Higher Farmer Retention',
    desc: 'Relationships persist beyond individual employee tenure.',
      icon: GroupOutlinedIcon,
  },
  {
    title: 'Complete Field Visibility',
    desc: 'Real-time view of every mandal, district, and village.',
    icon: Visibility,
  },
  {
    title: 'Stronger Managerial Control',
    desc: 'Hierarchical targets with full accountability chain.',
    icon: RadarOutlinedIcon,
  },
  {
    title: 'Improved Target Achievement',
    desc: 'No more restarting from zero every season.',
    icon: TrendingUpOutlinedIcon,
  },
];
const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: "#F6F8FF", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: 420, sm: 520, md: 600, lg: 700 },
          display: "flex",
          alignItems: "center",
          mb: { xs: 6, md: 10 },
        }}
      >
        <Box
          component="img"
          src={farmlandImg}
          alt="Farmland"
          loading="eager"
          sx={{
            position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(20, 22, 34, 0.60)",
            zIndex: 2,
          }}
        />
        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            zIndex: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: { xs: 420, sm: 520, md: 600, lg: 700 },
            py: { xs: 6, md: 10 },
          }}
        >
          <Typography
            component="h1"
            variant="h2"
            sx={{
              fontFamily: "Outfit, sans-serif",
              fontWeight: 800,
              color: "#fff",
              textAlign: "center",
              fontSize: {
                xs: "2rem",
                sm: "2.8rem",
                md: "3.2rem",
                lg: "3.6rem",
                xl: "4rem",
              },
              lineHeight: 1.1,
              mb: 1,
              textShadow: "0 2px 8px rgba(0,0,0,0.25)",
            }}
          >
            The Continuity-Driven CRM for
          </Typography>
          <Typography
            component="h2"
            variant="h2"
            sx={{
              fontFamily: "Outfit, sans-serif",
              fontWeight: 800,
              color: "#4F46E5",
              textAlign: "center",
              fontSize: {
                xs: "2.2rem",
                sm: "3rem",
                md: "3.5rem",
                lg: "4rem",
                xl: "4.5rem",
              },
              mb: 2,
              textShadow: "0 2px 8px rgba(0,0,0,0.10)",
            }}
          >
            Seed Companies
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "Outfit, sans-serif",
              color: "#fff",
              textAlign: "center",
              fontWeight: 400,
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.3rem" },
              mb: 4,
              textShadow: "0 1px 4px rgba(0,0,0,0.18)",
              maxWidth: 600,
            }}
          >
            Farmer360 ensures farmer relationships, commitments, and field
            intelligence stay with your company even when field staff change.
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              background: "#4F46E5",
              color: "#fff",
              fontWeight: 700,
              fontFamily: "Outfit, sans-serif",
              borderRadius: 2,
              px: 5,
              py: 1.5,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              boxShadow: "0 2px 8px rgba(79,70,229,0.18)",
              "&:hover": {
                background: "#3730A3",
                boxShadow: "0 6px 24px rgba(79,70,229,0.18)",
              },
              transition: "box-shadow 0.2s, background 0.2s",
            }}
            onClick={() => {
              navigate('/login')
            }}
            aria-label="Login"
          >
            LOGIN
          </Button>
        </Container>
      </Box>

      {/* Industry Problem Section */}
      <Grid>
        <Typography
          component="h2"
          variant="h3"
          sx={{
            fontFamily: "Outfit, sans-serif",
            fontWeight: 800,
            fontSize: {
              xs: "1.80rem",
              sm: "2.5rem",
              md: "3rem",
              lg: "3.4rem",
            },
            textAlign: "center",
            mb: { xs: 4, md: 6 },
            color: "#222",
            letterSpacing: "-1.5px",
            "& span": { color: "#4F46E5" },
          }}
        >
          The Industry <span>Problem</span>
        </Typography>
        <Grid
          container
          spacing={{ xs: 3, md: 4 }}
          sx={{
            px: { xs: 2, sm: 3, md: 7 },
          }}
        >
          {industryProblems.map((item) => (
            <Grid key={item.label} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  bgcolor: "#E6E0FF",
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  transition: "all 0.25s ease",
                  boxShadow: "0 2px 8px rgba(79,70,229,0.04)",
                  "&:hover": {
                    boxShadow: (theme) => theme.shadows[6],
                    transform: "translateY(-6px)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    p: { xs: 3, md: 3.5 },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#D1C4E9",
                      borderRadius: 2,
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    {item.iconType === "img" ? (
                      <img src={item.icon} alt={item.aria} style={{ width: 24, height: 24 }} />
                    ) : (
                      React.createElement(item.icon, { sx: { color: '#4F46E5', fontSize: 24 }, 'aria-label': item.aria })
                    )}
                  </Box>

                  <Typography
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      fontWeight: 700,
                      fontSize: { xs: "1.1rem", md: "1.18rem" },
                      mb: 1,
                      color: "#222",
                    }}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      color: "#222",
                      opacity: 0.8,
                      fontSize: { xs: "1rem", md: "1.05rem" },
                      flexGrow: 1,
                    }}
                  >
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
      {/* Farmer 360 Advantage Section */}
      <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
        <Box
          sx={{
            bgcolor: "#5646E5",
            borderRadius: { xs: "32px", md: "64px" },
            mx: { xs: 1, sm: 2, md: 6 },
            my: { xs: 5, md: 8 },
            py: { xs: 5, md: 8 },
            px: { xs: 2, sm: 4, md: 8 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            component="h2"
            variant="h3"
            sx={{
              fontFamily: "Outfit, sans-serif",
              fontWeight: 800,
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
                lg: "3.4rem",
              },
              textAlign: "center",
              mb: { xs: 4, md: 6 },
              color: "#fff",
              letterSpacing: "-1.5px",
            }}
          >
            The Farmer 360 Advantage
          </Typography>
          <Grid
            container
            spacing={{ xs: 2, md: 3 }}
            sx={{
              maxWidth: 1200,
              mx: "auto",
              justifyContent: "center",
            }}
          >
            {[
              {
                label: "Farmers are company-owned",
              },
              {
                label: "Mandal & District-based responsibility",
              },
              {
                label: "Commitment → Purchase tracking",
              },
              {
                label: "Invoice-backed confirmations",
              },
              {
                label: "Pre & Post season target control",
              },
              {
                label: "Zero-loss employee replacement",
              },
            ].map((item, idx) => (
              <Grid
                key={item.label}
                item
                size={{ xs: 12, sm: 6, md: 4 }}
                sx={{ display: "flex" }}
              >
                <Box
                  sx={{
                    bgcolor: item.dark ? "#2B2B2B" : "#FAFBF91A",
                    borderRadius: 4,
                    px: 4,
                    py: 2.5,
                    color: "#fff",
                    fontFamily: "Outfit, sans-serif",
                    fontWeight: 500,
                    fontSize: { xs: "1rem", md: "1.15rem" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    boxShadow: item.dark
                      ? "0 2px 8px rgba(0,0,0,0.10)"
                      : "0 2px 8px rgba(0,0,0,0.04)",
                    flex: 1,
                    width: "100%",
                    mt: idx >= 3 ? { xs: 0, md: 1 } : 0,
                  }}
                >
                  <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                    <img
                      src={yesicon}
                      alt="yes"
                      style={{ width: 22, height: 22 }}
                    />
                  </Box>
                  {item.label}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Grid>

      {/* built for real seed  */}
      <Box sx={{ bgcolor: "#F7F8FE", py: { xs: 6, md: 8 }, px: 2 }}>

        {/* ✅ HEADING */}
        <Typography
          variant="h3"
          sx={{
            fontFamily: "Outfit, sans-serif",
            fontWeight: 800,
            textAlign: "center",
            fontSize: { xs: "1.5rem", sm: "2.5rem", md: "2.8rem" },
            color: "#1F2937",
            width: { xs: "100%", sm: 500 },
            mx: "auto",
            mb: 4,
            letterSpacing: "-1px",
          }}
        >
          Built for Real Seed{" "}
          <Box component="span" sx={{ color: "#4F46E5" }}>
            Operations
          </Box>
        </Typography>

        {/* ✅ CARDS GRID */}
        <Grid
          container
          spacing={{ xs: 2, sm: 4 }}
          justifyContent="center"
          sx={{ mb: 6, px: { xs: 1, sm: 0 } }}
        >
          {[
            { title: "SO", subtitle: "District", icon: CorporateFareOutlinedIcon },
            { title: "MDO", subtitle: "Mandal", icon: LocationOnOutlinedIcon },
            { title: "Farmers", subtitle: "Village", icon: GroupOutlinedIcon },
          ].map((item) => (
            <Grid key={item.title} item xs={12} sm={4} md={3} >
              <Box
                sx={{
                  bgcolor: "#E8E9FB",
                  borderRadius: 3,
                  boxShadow: "0 4px 18px rgba(79,70,229,0.08)",
                  p: 3,
                  textAlign: "center",
                  width: { xs: 300, sm: "auto" },
                  mx: { xs: -2, sm: 0 },
                }}
              >
                {/* ICON */}
                <Box
                  sx={{
                    bgcolor: "#4F46E5",
                    borderRadius: "50%",
                    width: 64,
                    height: 64,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <item.icon sx={{ color: "#fff", fontSize: 32 }} />
                </Box>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    color: "#111827",
                    fontFamily: "Outfit, sans-serif",
                    mb: 0.5,
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  sx={{
                    color: "#6B7280",
                    fontFamily: "Outfit, sans-serif",
                    fontSize: "0.95rem",
                  }}
                >
                  Assigned to{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#111827" }}>
                    {item.subtitle}
                  </Box>
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* ✅ HIERARCHY BOX */}
        <Box
          sx={{
            bgcolor: "#E9EAFF",
            borderRadius: 4,
            px: { xs: 1.5, sm: 3, md: 10 },
            py: { xs: 2, md: 4 },
            maxWidth: { xs: "100%", sm: 720 },
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: "#4F46E5",
              fontWeight: 800,
              fontFamily: "Outfit, sans-serif",
              fontSize: "1.05rem",
              mb: 2,
            }}
          >
            Target Rollup Hierarchy
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mb: 1,
            }}
          >
            {["Mandal", "District", "AM", "RM", "ZM"].map((label, idx, arr) => (
              <React.Fragment key={label}>
                <Box
                  sx={{
                    bgcolor: "#DCDDFE",
                    color: "#4F46E5",
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 2,
                    py: 0.5,
                    fontFamily: "Outfit, sans-serif",
                    fontSize: "0.9rem",
                  }}
                >
                  {label}
                </Box>

                {idx < arr.length - 1 && (
                  <Box sx={{ color: "#9CA3AF", fontSize: 18 }}>{">"}</Box>
                )}
              </React.Fragment>
            ))}
          </Box>

          <Typography
            sx={{
              color: "#5946E5",
              fontFamily: "Outfit, sans-serif",
              fontSize: "0.95rem",
            }}
          >
            Everything structured. Nothing lost.
          </Typography>
        </Box>
      </Box>

      {/* what you gain */}
      <Box sx={{ bgcolor: '#F7F8FE', py: { xs: 6, md: 8 }, justifyContent: 'center', alignItems: 'center', mx: 'auto' }}>

        {/* Heading */}
        <Typography
          variant="h3"
          sx={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 800,
            textAlign: 'center',
            fontSize: { xs: '2rem', sm: '2.5rem', md: '2.8rem' },
            color: '#1F2937',
            mb: 6,
          }}
        >
          What You <Box component="span" sx={{ color: '#4F46E5' }}>Gain</Box>
        </Typography>

        <Grid container spacing={4} sx={{ maxWidth: 1300, mx: 'auto', justifyContent: 'center' }}>
          {[0, 1, 2].map(colIdx => (
            <Grid item xs={12} sm={6} md={4} key={colIdx}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {gainItems
                  .filter((_, idx) => idx % 3 === colIdx)
                  .map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        bgcolor: '#E9E8FF',
                        borderRadius: 3,
                        p: 3,
                        width: '100%',
                        maxWidth: 280,
                        minHeight: 170,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        boxShadow: '0 4px 14px rgba(79,70,229,0.06)',
                        transition: 'all .25s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 14px 28px rgba(79,70,229,0.12)',
                        },
                      }}
                    >
                      {/* ICON */}
                      <Box
                        sx={{
                          bgcolor: '#DAD9FF',
                          borderRadius: 2,
                          width: 42,
                          height: 42,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <item.icon sx={{ color: '#4F46E5', fontSize: 22 }} />
                      </Box>

                      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827', mb: .5 }}>
                        {item.title}
                      </Typography>

                      <Typography sx={{ color: '#6B7280', fontSize: '.95rem', lineHeight: 1.5 }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Grid>
          ))}
        </Grid>

      </Box>
    </Box>
  );
};

export default Home;
